---
title: Services
description: Every running part of the system and the one job it has.
---

## Frontend

The web app, built with TanStack Start and served by Bun on port 3001. It is
a thin client: all state lives behind the gateway's typed API. The settings
pages it renders are the only way store configuration is written.

## Gateway API

A Bun service on port 3000. It has four jobs:

1. **Accounts.** Better Auth with email and password. Sessions are JWTs the
   gateway itself issues and verifies against its own JWKS endpoint.
2. **Store configuration.** Every store, camera, calibration and zone lives
   in its Postgres. Saving a settings page validates the change and pushes it
   to the store's GPU machine.
3. **Run control.** Start a night, re-run a night, read run status and
   reports, all proxied to the GPU machine's control plane.
4. **Uploads.** Signs short-lived presigned URLs so the browser can put
   recordings directly into the store's bucket without the gateway touching
   the bytes.

## Postgres

One database for the operator app: auth tables and store configuration
together. It holds no video, no tracking data and no reports. It is
deliberately boring: small, transactional, easy to back up.

## Redis

Sessions and caching for the gateway. Nothing in it is precious.

## The object store

S3, MinIO or an SFTP server, depending on the store. It holds each night's
recordings under a fixed per-store, per-date layout. Credentials live in
environment variables on the machines that need them, never in settings.

## The ML control plane

A small HTTP service on each GPU machine, port 8000. The gateway is its only
client. It accepts configuration pushes, starts runs, reports progress, and
triggers calibration. It is the boundary between the app world and the video
world.

## The batch stages

Fetch, perception, analytics and review are not servers. They are programs
cron runs in order each night. Between runs they do not exist, and the GPU
sits idle. Each stage's inputs and outputs are plain files or Kafka messages,
so any stage can be re-run by hand.

## Kafka

Runs on the GPU machine. One topic carries per-frame tracking messages from
perception to analytics. It decouples the two stages: perception can finish
and release the GPU while analytics reads the topic at its own pace, and a
re-run of analytics does not need the GPU at all.

## MQTT

Runs on the GPU machine. During perception, the per-camera trackers negotiate
over MQTT which person is which across views. It is internal plumbing for the
tracker's peer mesh and carries nothing else.

## ClickHouse

Runs on the GPU machine, provisioned for analytics serving: the place
nightly results land as queryable rows, so dashboards can ask questions
across days and stores without re-reading report files. Why ClickHouse and
not something else is covered in
[Data and storage](/docs/data-and-storage).

## The calibration service

AMC, NVIDIA's automatic camera calibration, runs on the GPU machine but
outside the nightly chain. From a short piece of footage it works out where
every camera stands and how its pixels map onto the floor plan. Calibration
happens once per camera install, and again whenever a camera is moved or
bumped. Every meter-denominated number in the system depends on it.
